import React, { useState, useRef, useCallback } from 'react';
import {
    Upload,
    Download,
    X,
    FileText,
    AlertCircle,
    CheckCircle,
    Loader2,
    AlertTriangle,
    FileSpreadsheet,
    Copy,
    Info
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import targetService from '../../../api/crm/targets/targetService';
import { toast } from 'react-hot-toast';
import Papa from 'papaparse';

/**
 * BulkTargetUpload - Modal for bulk uploading targets from CSV
 * Modeled after JobBulkUploadModal.jsx design pattern
 */
const BulkTargetUpload = ({ isOpen, onComplete, onCancel }) => {
    const [step, setStep] = useState('upload'); // 'upload', 'preview', 'uploading', 'result'
    const [file, setFile] = useState(null);
    const [parsedTargets, setParsedTargets] = useState([]);
    const [previewData, setPreviewData] = useState(null);   // array from backend validation
    const [uploadResult, setUploadResult] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const fileInputRef = useRef(null);

    // ──────────────────────────────────────────────
    // Reset and close helpers
    // ──────────────────────────────────────────────
    const resetState = useCallback(() => {
        setStep('upload');
        setFile(null);
        setParsedTargets([]);
        setPreviewData(null);
        setUploadResult(null);
        setIsUploading(false);
        setIsValidating(false);
    }, []);

    const handleClose = useCallback(() => {
        resetState();
        onCancel();
    }, [resetState, onCancel]);

    // ──────────────────────────────────────────────
    // File handling
    // ──────────────────────────────────────────────
    const handleFileChange = async (e) => {
        const selectedFile = e.target.files?.[0];
        if (!selectedFile) return;

        const isValidType = selectedFile.type === 'text/csv' || selectedFile.name.endsWith('.csv');
        if (!isValidType) {
            toast.error('Please upload a CSV file');
            return;
        }

        if (selectedFile.size > 5 * 1024 * 1024) {
            toast.error('File size must be less than 5MB');
            return;
        }

        setFile(selectedFile);

        Papa.parse(selectedFile, {
            header: true,
            skipEmptyLines: true,
            complete: async (results) => {
                if (!results.data || results.data.length === 0) {
                    toast.error('No valid target data found in the CSV');
                    setFile(null);
                    return;
                }

                if (results.data.length > 200) {
                    toast.error('Maximum 200 targets per upload. Please split your file.');
                    setFile(null);
                    return;
                }

                // Normalize column names (trim whitespace)
                // Use String() conversion before .trim() because PapaParse can return
                // numeric values for numeric-looking cells, which don't have .trim()
                const normalized = results.data.map(row => {
                    const clean = {};
                    Object.keys(row).forEach(k => { clean[k.trim()] = String(row[k] == null ? '' : row[k]).trim(); });
                    return clean;
                });

                setParsedTargets(normalized);
                setStep('preview');
                await validateTargets(normalized);
            },
            error: () => {
                toast.error('Failed to read CSV file');
                setFile(null);
            }
        });
    };

    // ──────────────────────────────────────────────
    // Validate via backend
    // ──────────────────────────────────────────────
    const validateTargets = async (targets) => {
        setIsValidating(true);
        try {
            const response = await targetService.bulkPreview(targets);
            if (response.success) {
                setPreviewData(response.data);
            } else {
                toast.error(response.error || 'Validation failed');
            }
        } catch (err) {
            toast.error('Validation failed: ' + (err.message || 'Unknown error'));
        } finally {
            setIsValidating(false);
        }
    };

    // ──────────────────────────────────────────────
    // Drag & drop
    // ──────────────────────────────────────────────
    const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const droppedFile = e.dataTransfer?.files?.[0];
        if (droppedFile) handleFileChange({ target: { files: [droppedFile] } });
    };

    // ──────────────────────────────────────────────
    // Upload to backend
    // ──────────────────────────────────────────────
    const handleUpload = async () => {
        if (!previewData || validTargetsCount <= 0) return;

        setIsUploading(true);
        setStep('uploading');
        try {
            const validTargets = previewData.filter(r => r.isValid).map(r => r.resolvedData);
            const response = await targetService.bulkUpload(validTargets);
            setUploadResult(response);
            setStep('result');

            if (response.success) {
                const created = response.data?.success || 0;
                toast.success(`Successfully created ${created} target${created !== 1 ? 's' : ''}`);
                if (response.data?.failed > 0) {
                    toast.error(`${response.data.failed} target(s) failed`);
                }
            } else {
                toast.error(response.error || 'Upload failed');
            }
        } catch (err) {
            const errMsg = err.message || 'Upload failed';
            toast.error(errMsg);
            setUploadResult({ success: false, error: errMsg });
            setStep('result');
        } finally {
            setIsUploading(false);
        }
    };

    // ──────────────────────────────────────────────
    // Template download
    // ──────────────────────────────────────────────
    const handleDownloadTemplate = () => {
        const headers = ['targetName', 'type', 'period', 'scope', 'value', 'startDate', 'endDate', 'employeeIds', 'teamName', 'baselineRules'];
        const samples = [
            ['Q2 Revenue Target', 'revenue', 'monthly', 'individual', '500000', '2024-04-01', '', 'EMP001', '', 'Focus on enterprise deals'],
            ['Q2 Deal Count', 'deals', 'quarterly', 'individual', '20', '2024-04-01', '', 'EMP002', '', ''],
            ['Team Opps Target', 'newOpps', 'custom', 'team', '50', '2024-04-01', '2024-06-30', 'EMP001,EMP003', 'North Sales Team', 'New verticals only'],
        ];

        // Escape each field with double quotes to handle commas within values (like multiple employee IDs)
        const csvContent = [headers, ...samples]
            .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
            .join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'target_bulk_upload_template.csv';
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Template downloaded');
    };

    // ──────────────────────────────────────────────
    // Back navigation
    // ──────────────────────────────────────────────
    const handleBack = () => {
        if (step === 'preview') {
            setStep('upload');
            setFile(null);
            setParsedTargets([]);
            setPreviewData(null);
        } else if (step === 'result') {
            handleClose();
        }
    };

    // ──────────────────────────────────────────────
    // Derived counts
    // ──────────────────────────────────────────────
    const errorCount = previewData ? previewData.filter(r => !r.isValid).length : 0;
    const validTargetsCount = previewData ? previewData.filter(r => r.isValid).length : 0;
    const totalParsed = parsedTargets.length;

    if (!isOpen) return null;

    // ──────────────────────────────────────────────
    // Render
    // ──────────────────────────────────────────────
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/50" onClick={handleClose} />

            {/* Modal */}
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">

                {/* ── Header ── */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-50 rounded-lg">
                            <Upload className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Bulk Upload Targets</h2>
                            <p className="text-sm text-gray-500">
                                {step === 'upload' && 'Upload a CSV file with target data'}
                                {step === 'preview' && (isValidating
                                    ? 'Validating your data…'
                                    : `${validTargetsCount} of ${totalParsed} targets ready for upload`)}
                                {step === 'uploading' && 'Uploading targets…'}
                                {step === 'result' && 'Upload complete'}
                            </p>
                        </div>
                    </div>
                    <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                        <X className="h-5 w-5 text-gray-500" />
                    </button>
                </div>

                {/* ── Content ── */}
                <div className="flex-1 overflow-y-auto p-6">

                    {/* ── STEP: upload ── */}
                    {step === 'upload' && (
                        <div className="space-y-6">
                            {/* Drop Zone */}
                            <div
                                className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-500 hover:bg-blue-50/30 transition-all cursor-pointer"
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileChange} className="hidden" />
                                <FileSpreadsheet className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                                <p className="text-lg font-medium text-gray-700 mb-2">Drag & drop your CSV file here</p>
                                <p className="text-sm text-gray-500 mb-4">or click to browse (Max 200 targets, 5 MB)</p>
                                <Button variant="outline" icon={<Upload className="h-4 w-4" />}>Select File</Button>
                            </div>

                            {/* Template download */}
                            <div className="bg-blue-50 rounded-xl p-4 flex items-start space-x-4">
                                <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                                    <FileText className="h-5 w-5 text-blue-600" />
                                </div>
                                <div className="flex-1">
                                    <h4 className="text-sm font-medium text-blue-900 mb-1">Need a template?</h4>
                                    <p className="text-sm text-blue-700 mb-3">
                                        Download the CSV template with all required fields and sample rows.
                                    </p>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        icon={<Download className="h-4 w-4" />}
                                        onClick={handleDownloadTemplate}
                                        className="border-blue-300 text-blue-700 hover:bg-blue-100"
                                    >
                                        Download Template
                                    </Button>
                                </div>
                            </div>

                            {/* Field reference */}
                            <div className="bg-gray-50 rounded-xl p-4">
                                <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                                    <Info className="h-4 w-4 mr-1.5 text-gray-500" />
                                    Field Guidelines
                                </h4>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Required Columns</p>
                                        <ul className="text-sm text-gray-700 space-y-1">
                                            {[
                                                { name: 'targetName', hint: 'Any descriptive label' },
                                                { name: 'type', hint: 'revenue | deals | newOpps | activities' },
                                                { name: 'period', hint: 'monthly | quarterly | yearly | custom' },
                                                { name: 'scope', hint: 'individual | team' },
                                                { name: 'value', hint: 'Positive number' },
                                                { name: 'startDate', hint: 'YYYY-MM-DD' },
                                                { name: 'employeeIds', hint: 'Employee ID, comma-separated for team' },
                                            ].map(f => (
                                                <li key={f.name} className="flex items-start">
                                                    <span className="w-1.5 h-1.5 bg-red-500 rounded-full mr-2 mt-1.5 flex-shrink-0" />
                                                    <span><strong>{f.name}</strong> <span className="text-gray-500 text-xs">({f.hint})</span></span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div>
                                        <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Optional Columns</p>
                                        <ul className="text-sm text-gray-700 space-y-1">
                                            {[
                                                { name: 'endDate', hint: 'Required if period = custom' },
                                                { name: 'teamName', hint: 'Label for team scope' },
                                                { name: 'baselineRules', hint: 'Any notes / rules' },
                                            ].map(f => (
                                                <li key={f.name} className="flex items-start">
                                                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mr-2 mt-1.5 flex-shrink-0" />
                                                    <span><strong>{f.name}</strong> <span className="text-gray-500 text-xs">({f.hint})</span></span>
                                                </li>
                                            ))}
                                        </ul>
                                        <div className="pt-3 mt-3 border-t border-gray-200">
                                            <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Notes</p>
                                            <p className="text-xs text-gray-600">
                                                For <strong>team</strong> scope, provide multiple employee IDs
                                                separated by commas (e.g. <code className="bg-gray-200 px-1 rounded">EMP001,EMP002</code>).<br />
                                                Revenue value supports decimals; deals / newOpps must be whole numbers.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── STEP: preview ── */}
                    {step === 'preview' && (
                        <div className="space-y-5">
                            {/* File info bar */}
                            <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
                                <div className="flex items-center space-x-3">
                                    <FileSpreadsheet className="h-8 w-8 text-blue-500" />
                                    <div>
                                        <p className="font-medium text-gray-900">{file?.name}</p>
                                        <p className="text-sm text-gray-500">{totalParsed} target{totalParsed !== 1 ? 's' : ''} parsed</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => { setFile(null); setParsedTargets([]); setPreviewData(null); setStep('upload'); }}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>

                            {/* Validation spinner */}
                            {isValidating ? (
                                <div className="flex items-center justify-center p-10">
                                    <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                                    <span className="ml-3 text-gray-600">Validating targets with server…</span>
                                </div>
                            ) : previewData && (
                                <div className="space-y-4">
                                    {/* Summary cards */}
                                    <div className="grid grid-cols-3 gap-3">
                                        <div className="bg-blue-50 rounded-lg p-3 text-center">
                                            <p className="text-2xl font-bold text-blue-700">{totalParsed}</p>
                                            <p className="text-xs text-blue-600 mt-0.5">Total Parsed</p>
                                        </div>
                                        <div className={`rounded-lg p-3 text-center ${errorCount > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
                                            <AlertCircle className={`h-5 w-5 mx-auto mb-1 ${errorCount > 0 ? 'text-red-500' : 'text-gray-300'}`} />
                                            <p className={`text-2xl font-bold ${errorCount > 0 ? 'text-red-700' : 'text-gray-400'}`}>{errorCount}</p>
                                            <p className={`text-xs mt-0.5 ${errorCount > 0 ? 'text-red-600' : 'text-gray-400'}`}>Errors</p>
                                        </div>
                                        <div className={`rounded-lg p-3 text-center ${validTargetsCount > 0 ? 'bg-green-50' : 'bg-gray-50'}`}>
                                            <CheckCircle className={`h-5 w-5 mx-auto mb-1 ${validTargetsCount > 0 ? 'text-green-500' : 'text-gray-300'}`} />
                                            <p className={`text-2xl font-bold ${validTargetsCount > 0 ? 'text-green-700' : 'text-gray-400'}`}>{validTargetsCount}</p>
                                            <p className={`text-xs mt-0.5 ${validTargetsCount > 0 ? 'text-green-600' : 'text-gray-400'}`}>Ready to Upload</p>
                                        </div>
                                    </div>

                                    {/* Error detail list */}
                                    {errorCount > 0 && (
                                        <div className="bg-red-50 rounded-lg p-4">
                                            <div className="flex items-center justify-between mb-2">
                                                <h4 className="text-sm font-medium text-red-800 flex items-center">
                                                    <AlertTriangle className="h-4 w-4 mr-1.5" />
                                                    Validation Errors
                                                </h4>
                                                <button
                                                    onClick={() => {
                                                        const errText = previewData
                                                            .filter(r => !r.isValid)
                                                            .map((r, i) => `Row ${i + 1}: ${r.errors.join('; ')}`)
                                                            .join('\n');
                                                        navigator.clipboard.writeText(errText);
                                                        toast.success('Errors copied to clipboard');
                                                    }}
                                                    className="flex items-center space-x-1 text-xs text-red-600 hover:text-red-800 bg-red-100 hover:bg-red-200 px-2 py-1 rounded transition-colors"
                                                    title="Copy all errors"
                                                >
                                                    <Copy className="h-3 w-3" />
                                                    <span>Copy All</span>
                                                </button>
                                            </div>
                                            <div className="max-h-36 overflow-y-auto space-y-1.5">
                                                {previewData.filter(r => !r.isValid).slice(0, 10).map((row, idx) => (
                                                    <div key={idx} className="text-sm text-red-700 bg-red-100/60 p-2 rounded border border-red-200">
                                                        <span className="font-bold mr-1">{row.targetName || `Row ${idx + 1}`}:</span>
                                                        {row.errors.join(', ')}
                                                    </div>
                                                ))}
                                                {errorCount > 10 && (
                                                    <p className="text-sm text-red-600 font-medium px-2">
                                                        … and {errorCount - 10} more error{errorCount - 10 !== 1 ? 's' : ''}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Preview table */}
                                    <div className="border rounded-lg overflow-hidden">
                                        <div className="bg-gray-50 px-4 py-2 border-b flex items-center justify-between">
                                            <h4 className="text-sm font-medium text-gray-900">Preview (first 5 targets)</h4>
                                            {validTargetsCount === 0 && (
                                                <span className="text-xs text-red-600 font-medium">No valid targets to upload</span>
                                            )}
                                        </div>
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Target Name</th>
                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Assignee(s)</th>
                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {previewData.slice(0, 5).map((row, idx) => (
                                                        <tr key={idx} className={row.isValid ? '' : 'bg-red-50'}>
                                                            <td className="px-4 py-2">
                                                                {row.isValid
                                                                    ? <CheckCircle className="h-4 w-4 text-green-500" />
                                                                    : <AlertCircle className="h-4 w-4 text-red-500" />}
                                                            </td>
                                                            <td className="px-4 py-2 font-medium text-gray-900 max-w-[120px] truncate" title={row.targetName}>
                                                                {row.targetName || '—'}
                                                            </td>
                                                            <td className="px-4 py-2 text-gray-600 capitalize">{row.type || '—'}</td>
                                                            <td className="px-4 py-2 text-gray-600 capitalize">{row.period || '—'}</td>
                                                            <td className="px-4 py-2 text-gray-600 max-w-[140px] truncate" title={row.resolvedAssigneeName || row.resolvedTeamNames?.join(', ')}>
                                                                {row.resolvedAssigneeName || row.resolvedTeamNames?.join(', ') || row.employeeIds || '—'}
                                                            </td>
                                                            <td className="px-4 py-2 text-gray-900 font-medium">
                                                                {row.type === 'revenue' ? `₹${Number(row.value || 0).toLocaleString('en-IN')}` : row.value || '—'}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── STEP: uploading ── */}
                    {step === 'uploading' && (
                        <div className="flex flex-col items-center justify-center py-16">
                            <Loader2 className="h-12 w-12 animate-spin text-blue-500 mb-4" />
                            <p className="text-lg font-medium text-gray-900 mb-2">Uploading Targets…</p>
                            <p className="text-sm text-gray-500">Please wait while we process your data</p>
                        </div>
                    )}

                    {/* ── STEP: result ── */}
                    {step === 'result' && uploadResult && (
                        <div className="space-y-6">
                            <div className={`text-center py-8 rounded-xl ${uploadResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
                                {uploadResult.success
                                    ? <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                                    : <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                                }
                                <h3 className={`text-xl font-semibold mb-2 ${uploadResult.success ? 'text-green-800' : 'text-red-800'}`}>
                                    {uploadResult.success ? 'Upload Successful!' : 'Upload Failed'}
                                </h3>
                                <p className={`text-sm ${uploadResult.success ? 'text-green-600' : 'text-red-600'}`}>
                                    {uploadResult.error || (uploadResult.success ? 'Your targets have been created.' : '')}
                                </p>
                            </div>

                            {uploadResult.success && uploadResult.data && (
                                <div className="grid grid-cols-3 gap-3">
                                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                                        <p className="text-2xl font-bold text-gray-700">{uploadResult.data.total || 0}</p>
                                        <p className="text-sm text-gray-500">Total</p>
                                    </div>
                                    <div className="bg-green-50 rounded-lg p-4 text-center">
                                        <p className="text-2xl font-bold text-green-700">{uploadResult.data.success || 0}</p>
                                        <p className="text-sm text-green-600">Created</p>
                                    </div>
                                    <div className="bg-red-50 rounded-lg p-4 text-center">
                                        <p className="text-2xl font-bold text-red-700">{uploadResult.data.failed || 0}</p>
                                        <p className="text-sm text-red-600">Failed</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* ── Footer ── */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                    {/* Left: back / cancel */}
                    <div className="flex items-center space-x-3">
                        {step === 'preview' && (
                            <Button variant="outline" onClick={handleBack}>Back</Button>
                        )}
                        {step !== 'result' && step !== 'uploading' && (
                            <Button variant="outline" onClick={handleClose}>Cancel</Button>
                        )}
                    </div>

                    {/* Right: action button */}
                    {step === 'preview' && !isValidating && (
                        <div className="flex items-center space-x-3">
                            {validTargetsCount === 0 && (
                                <span className="text-sm text-red-600">No valid targets to upload</span>
                            )}
                            <Button
                                variant="primary"
                                icon={isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                                onClick={handleUpload}
                                disabled={isUploading || validTargetsCount === 0}
                                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400"
                            >
                                {isUploading ? 'Uploading…' : `Upload ${validTargetsCount} Target${validTargetsCount !== 1 ? 's' : ''}`}
                            </Button>
                        </div>
                    )}

                    {step === 'result' && (
                        <Button
                            variant="primary"
                            onClick={() => { if (uploadResult?.success) { onComplete(); } handleClose(); }}
                            className="bg-blue-600 hover:bg-blue-700"
                        >
                            Done
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BulkTargetUpload;
